import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Book,
  Server,
  Layout as LayoutIcon,
  Users,
  Plug,
  Code,
  History,
  Map,
  Download,
  ExternalLink,
  ChevronRight,
  Database,
  Share2,
  Link,
  Copy,
  Clock,
  Trash2,
  Eye,
  EyeOff,
  Lock,
  FileText,
  FileCode,
  FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Documentation = () => {
  const { isDarkMode } = useTheme();
  const { user, token, isSuperAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  
  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareValidity, setShareValidity] = useState('24h');
  const [shareLoading, setShareLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [existingShares, setExistingShares] = useState([]);
  const [showSharePassword, setShowSharePassword] = useState(false);
  
  // Last update timestamp
  const LAST_UPDATE = new Date('2026-01-22T12:30:00Z');
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load existing shares
  useEffect(() => {
    if (isSuperAdmin && token) {
      loadExistingShares();
    }
  }, [isSuperAdmin, token]);

  const loadExistingShares = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/admin/doc-shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExistingShares(data.shares || []);
      }
    } catch (error) {
      console.error('Error loading shares:', error);
    }
  };

  const createShareLink = async () => {
    if (!sharePassword || sharePassword.length < 4) {
      toast.error('Le mot de passe doit faire au moins 4 caractères');
      return;
    }
    
    setShareLoading(true);
    try {
      // Generate HTML content for the documentation
      const docContent = generateDocumentationHTML();
      
      const response = await fetch(`${API_URL}/api/v2/admin/doc-share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          password: sharePassword,
          validity: shareValidity,
          content: docContent,
          title: 'Documentation B2RCS'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const shareUrl = `${window.location.origin}/doc-share/${data.token}`;
        setGeneratedLink(shareUrl);
        toast.success('Lien de partage créé !');
        loadExistingShares();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setShareLoading(false);
    }
  };

  // Generate COMPLETE HTML content for sharing with all sections
  const generateDocumentationHTML = () => {
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Documentation B2RCS</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background: #f8fafc; }
    .container { display: flex; min-height: 100vh; }
    .sidebar { width: 280px; background: #1e293b; color: white; padding: 20px; position: fixed; height: 100vh; overflow-y: auto; }
    .sidebar h2 { font-size: 18px; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #334155; }
    .sidebar a { display: block; padding: 10px 15px; color: #94a3b8; text-decoration: none; border-radius: 8px; margin-bottom: 5px; transition: all 0.2s; }
    .sidebar a:hover { background: #334155; color: white; }
    .sidebar a.active { background: #4f46e5; color: white; }
    .main { margin-left: 280px; flex: 1; padding: 40px; max-width: 900px; }
    h1 { color: #1e293b; border-bottom: 3px solid #4f46e5; padding-bottom: 15px; }
    h2 { color: #334155; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
    h3 { color: #475569; margin-top: 25px; }
    p { line-height: 1.7; color: #475569; }
    code { background: #f1f5f9; padding: 3px 8px; border-radius: 4px; font-size: 13px; color: #7c3aed; }
    pre { background: #1e293b; color: #e2e8f0; padding: 20px; border-radius: 8px; overflow-x: auto; }
    pre code { background: transparent; color: inherit; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; }
    th { background: #f8fafc; font-weight: 600; }
    ul, ol { padding-left: 25px; line-height: 1.8; }
    li { margin: 8px 0; }
    .badge { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; }
    .badge-blue { background: #dbeafe; color: #1d4ed8; }
    .badge-green { background: #dcfce7; color: #16a34a; }
    .badge-purple { background: #f3e8ff; color: #7c3aed; }
    .info-box { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 15px; margin: 20px 0; }
    .info-box.warning { background: #fef3c7; border-color: #fcd34d; }
    .section { scroll-margin-top: 20px; }
    @media (max-width: 768px) {
      .sidebar { position: relative; width: 100%; height: auto; }
      .main { margin-left: 0; }
      .container { flex-direction: column; }
    }
  </style>
</head>
<body>
  <div class="container">
    <nav class="sidebar">
      <h2>📚 Documentation B2RCS</h2>
      <a href="#overview">📋 Vue d'ensemble</a>
      <a href="#architecture">🏗️ Architecture</a>
      <a href="#database">💾 Base de données</a>
      <a href="#modules">📦 Modules Backend</a>
      <a href="#frontend">🎨 Frontend</a>
      <a href="#integrations">🔌 Intégrations</a>
      <a href="#roles">👥 Rôles & Permissions</a>
      <a href="#api">📡 API Reference</a>
      <a href="#changelog">📝 Changelog</a>
    </nav>
    
    <main class="main">
      <h1>Documentation B2RCS</h1>
      <p><strong>Dernière mise à jour:</strong> ${formatDate(LAST_UPDATE)}</p>
      
      <!-- VUE D'ENSEMBLE -->
      <section id="overview" class="section">
        <h2>📋 Vue d'ensemble</h2>
        <p>B2RCS est une plateforme de gestion de campagnes RCS (Rich Communication Services) et SMS, permettant aux entreprises d'envoyer des messages enrichis à leurs clients.</p>
        
        <h3>Fonctionnalités principales</h3>
        <ul>
          <li>✅ Gestion multi-clients avec isolation des données</li>
          <li>✅ Templates RCS personnalisables avec aperçu temps réel</li>
          <li>✅ Campagnes programmées et récurrentes</li>
          <li>✅ SMS Fallback automatique via B2SMS</li>
          <li>✅ Tableau de bord analytique complet</li>
          <li>✅ Gestion des licences et quotas</li>
          <li>✅ Système de blacklist STOP/UNSUB</li>
          <li>✅ Module SMS Mailing avec gestion des campagnes</li>
          <li>✅ Règles de réécriture de numéros configurables</li>
          <li>✅ Gestion des fuseaux horaires par client</li>
        </ul>
      </section>
      
      <!-- ARCHITECTURE -->
      <section id="architecture" class="section">
        <h2>🏗️ Architecture</h2>
        
        <h3>Stack Technique</h3>
        <table>
          <tr><th>Composant</th><th>Technologie</th></tr>
          <tr><td>Frontend</td><td>React 18 + Tailwind CSS + shadcn/ui</td></tr>
          <tr><td>Backend</td><td>FastAPI (Python 3.11)</td></tr>
          <tr><td>Base de données</td><td>MongoDB</td></tr>
          <tr><td>Intégrations</td><td>B2SMS API, Resend (emails)</td></tr>
          <tr><td>Authentification</td><td>JWT + Bcrypt</td></tr>
        </table>
        
        <h3>Structure des dossiers</h3>
        <pre><code>/app
├── backend/
│   ├── modules/        # Modules métier
│   │   ├── templates/  # Gestion templates RCS
│   │   ├── campaigns/  # Orchestration campagnes
│   │   ├── analytics/  # Statistiques
│   │   ├── sms_mailing/# SMS via B2SMS
│   │   └── admin/      # Administration
│   ├── services/       # Services partagés
│   ├── models.py       # Modèles Pydantic
│   └── server.py       # Point d'entrée FastAPI
└── frontend/
    └── src/
        ├── components/ # Composants React
        ├── pages/      # Pages de l'application
        └── contexts/   # Contextes React</code></pre>
      </section>
      
      <!-- BASE DE DONNÉES -->
      <section id="database" class="section">
        <h2>💾 Base de données</h2>
        
        <h3>Collections MongoDB</h3>
        <table>
          <tr><th>Collection</th><th>Description</th></tr>
          <tr><td>users</td><td>Utilisateurs et authentification</td></tr>
          <tr><td>clients</td><td>Clients (multi-tenant)</td></tr>
          <tr><td>templates</td><td>Templates RCS</td></tr>
          <tr><td>campaigns</td><td>Campagnes RCS</td></tr>
          <tr><td>sms_campaigns</td><td>Campagnes SMS</td></tr>
          <tr><td>blacklist</td><td>Numéros STOP/UNSUB</td></tr>
          <tr><td>settings</td><td>Configuration globale</td></tr>
          <tr><td>doc_shares</td><td>Liens de partage documentation</td></tr>
        </table>
        
        <h3>Schéma User</h3>
        <pre><code>{
  "id": "string (UUID)",
  "email": "string",
  "password": "string (bcrypt hash)",
  "role": "superadmin | admin | user",
  "client_id": "string (ObjectId)",
  "created_at": "datetime",
  "last_login": "datetime"
}</code></pre>
        
        <h3>Schéma Client</h3>
        <pre><code>{
  "id": "string",
  "name": "string",
  "sms_config": {
    "credentials": {
      "login": "string",
      "password": "string (encrypted)"
    },
    "service_url": "string"
  },
  "timezone": "string (IANA)",
  "quota": { "sms": number, "rcs": number }
}</code></pre>
      </section>
      
      <!-- MODULES BACKEND -->
      <section id="modules" class="section">
        <h2>📦 Modules Backend</h2>
        
        <h3>/modules/templates</h3>
        <p>Gestion des templates RCS avec contenu riche (images, boutons, carrousels).</p>
        <ul>
          <li><code>GET /api/templates</code> - Liste des templates</li>
          <li><code>POST /api/templates</code> - Créer un template</li>
          <li><code>GET /api/templates/library</code> - Bibliothèque système (42 templates)</li>
        </ul>
        
        <h3>/modules/campaigns</h3>
        <p>Orchestration des campagnes RCS avec programmation et récurrence.</p>
        <ul>
          <li><code>GET /api/campaigns</code> - Liste des campagnes</li>
          <li><code>POST /api/campaigns</code> - Créer une campagne</li>
          <li><code>POST /api/campaigns/{id}/send</code> - Envoyer une campagne</li>
        </ul>
        
        <h3>/modules/sms_mailing</h3>
        <p>Envoi de SMS via l'API B2SMS avec gestion des règles de réécriture.</p>
        <ul>
          <li><code>POST /api/sms-mailing/send</code> - Envoyer une campagne SMS</li>
          <li><code>POST /api/sms-mailing/send-test-direct</code> - Test d'envoi SMS</li>
          <li><code>GET /api/sms-mailing/campaigns</code> - Archives des campagnes</li>
        </ul>
        
        <h3>/modules/admin</h3>
        <p>Administration système (SUPADMIN uniquement).</p>
        <ul>
          <li><code>GET /api/v2/admin/logs</code> - Logs système</li>
          <li><code>GET/POST /api/v2/admin/phone-rewrite-rules</code> - Règles de réécriture</li>
          <li><code>GET/POST /api/v2/admin/settings/timezone</code> - Fuseau horaire global</li>
        </ul>
      </section>
      
      <!-- FRONTEND -->
      <section id="frontend" class="section">
        <h2>🎨 Frontend</h2>
        
        <h3>Pages principales</h3>
        <table>
          <tr><th>Page</th><th>Route</th><th>Description</th></tr>
          <tr><td>Dashboard</td><td>/</td><td>Tableau de bord analytique</td></tr>
          <tr><td>Campagnes</td><td>/campaigns</td><td>Liste et gestion des campagnes</td></tr>
          <tr><td>Templates</td><td>/templates</td><td>Bibliothèque de templates</td></tr>
          <tr><td>SMS Mailing</td><td>/sms-mailing</td><td>Envoi de campagnes SMS</td></tr>
          <tr><td>Configuration</td><td>/onboarding-hub</td><td>Paramètres et configuration</td></tr>
          <tr><td>Logs</td><td>/logs</td><td>Visualiseur de logs (SUPADMIN)</td></tr>
          <tr><td>Documentation</td><td>/documentation</td><td>Cette documentation</td></tr>
        </table>
        
        <h3>Composants UI</h3>
        <p>L'application utilise <strong>shadcn/ui</strong> avec les composants suivants :</p>
        <ul>
          <li>Button, Card, Badge, Input, Select</li>
          <li>Dialog, Sheet, Tabs, Toast</li>
          <li>Table, Calendar, Switch</li>
        </ul>
      </section>
      
      <!-- INTÉGRATIONS -->
      <section id="integrations" class="section">
        <h2>🔌 Intégrations</h2>
        
        <h3>B2SMS API</h3>
        <div class="info-box">
          <strong>URL:</strong> https://www.b2sms.com/v1<br>
          <strong>Auth:</strong> HTTP Basic (login/password)<br>
          <strong>Format numéro:</strong> MSISDN avec + (+262692XXXXXX)
        </div>
        
        <h4>Endpoints utilisés</h4>
        <table>
          <tr><th>Méthode</th><th>Endpoint</th><th>Description</th></tr>
          <tr><td>GET</td><td>/ping</td><td>Vérification des credentials</td></tr>
          <tr><td>POST</td><td>/sms</td><td>Envoi d'un SMS unitaire</td></tr>
        </table>
        
        <h4>Exemple de requête</h4>
        <pre><code>POST https://www.b2sms.com/v1/sms
Authorization: Basic base64(login:password)
Content-Type: application/json

{
  "to": "+262692123456",
  "text": "Votre message",
  "senderId": "B2RCS"
}</code></pre>
        
        <h3>Resend (Emails)</h3>
        <p>Utilisé pour les notifications et réinitialisations de mot de passe.</p>
      </section>
      
      <!-- RÔLES & PERMISSIONS -->
      <section id="roles" class="section">
        <h2>👥 Rôles & Permissions</h2>
        
        <table>
          <tr>
            <th>Rôle</th>
            <th>Description</th>
            <th>Accès</th>
          </tr>
          <tr>
            <td><span class="badge badge-purple">SUPADMIN</span></td>
            <td>Super administrateur plateforme</td>
            <td>Tous les clients, configuration globale, logs système, règles de réécriture, timezone</td>
          </tr>
          <tr>
            <td><span class="badge badge-blue">admin</span></td>
            <td>Administrateur client</td>
            <td>Gestion de son client uniquement, templates, campagnes</td>
          </tr>
          <tr>
            <td><span class="badge badge-green">user</span></td>
            <td>Utilisateur standard</td>
            <td>Création de campagnes, consultation analytics</td>
          </tr>
        </table>
      </section>
      
      <!-- API REFERENCE -->
      <section id="api" class="section">
        <h2>📡 API Reference</h2>
        
        <h3>Authentification</h3>
        <pre><code>POST /api/auth/login
Content-Type: application/json

{
  "email": "user@example.com",
  "password": "password"
}

Response: { "access_token": "jwt_token", "user": {...} }</code></pre>
        
        <h3>Headers requis</h3>
        <pre><code>Authorization: Bearer {access_token}
Content-Type: application/json</code></pre>
        
        <h3>Codes de réponse</h3>
        <table>
          <tr><th>Code</th><th>Description</th></tr>
          <tr><td>200</td><td>Succès</td></tr>
          <tr><td>400</td><td>Requête invalide</td></tr>
          <tr><td>401</td><td>Non authentifié</td></tr>
          <tr><td>403</td><td>Non autorisé</td></tr>
          <tr><td>404</td><td>Ressource non trouvée</td></tr>
          <tr><td>500</td><td>Erreur serveur</td></tr>
        </table>
      </section>
      
      <!-- CHANGELOG -->
      <section id="changelog" class="section">
        <h2>📝 Changelog</h2>
        
        <h3>Janvier 2026</h3>
        <ul>
          <li>✅ Module SMS Mailing avec intégration B2SMS complète</li>
          <li>✅ Partage de documentation protégé par mot de passe</li>
          <li>✅ Gestion des règles de réécriture de numéros (drag & drop)</li>
          <li>✅ Configuration des fuseaux horaires (IANA complet)</li>
          <li>✅ Visualiseur de logs système</li>
          <li>✅ Mode Dark/Light avec sauvegarde</li>
          <li>✅ Édition de campagnes existantes</li>
          <li>✅ Export documentation (PDF, Markdown, HTML)</li>
        </ul>
      </section>
      
      <hr style="margin-top: 40px;">
      <p style="color: #94a3b8; font-size: 12px; text-align: center;">
        Documentation B2RCS • Générée automatiquement<br>
        Pour toute question, contactez votre administrateur.
      </p>
    </main>
  </div>
</body>
</html>
    `;
  };

  // Export functions
  const exportToPDF = () => {
    // Generate printable version and trigger print dialog for PDF
    const content = generateDocumentationHTML();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Documentation B2RCS</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e293b; border-bottom: 2px solid #4f46e5; padding-bottom: 10px; }
          h2 { color: #334155; margin-top: 30px; }
          h3 { color: #475569; }
          code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
          table { border-collapse: collapse; width: 100%; margin: 15px 0; }
          th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; }
          th { background: #f8fafc; }
          ul { padding-left: 20px; }
          li { margin: 5px 0; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    toast.success('Impression PDF lancée');
  };

  const exportToMarkdown = () => {
    const markdown = `# Documentation B2RCS

**Dernière mise à jour:** ${formatDate(LAST_UPDATE)}

## 📋 Vue d'ensemble

B2RCS est une plateforme de gestion de campagnes RCS (Rich Communication Services) et SMS, permettant aux entreprises d'envoyer des messages enrichis à leurs clients.

### Fonctionnalités principales
- ✅ Gestion multi-clients avec isolation des données
- ✅ Templates RCS personnalisables avec aperçu temps réel
- ✅ Campagnes programmées et récurrentes
- ✅ SMS Fallback automatique via B2SMS
- ✅ Tableau de bord analytique complet
- ✅ Gestion des licences et quotas
- ✅ Système de blacklist STOP/UNSUB

## 🏗️ Architecture

### Stack Technique
- **Frontend:** React 18 + Tailwind CSS + shadcn/ui
- **Backend:** FastAPI (Python 3.11)
- **Base de données:** MongoDB
- **Intégrations:** B2SMS API, Resend (emails)

### Structure des modules Backend
- \`/modules/templates\` - Gestion des templates RCS
- \`/modules/campaigns\` - Orchestration des campagnes
- \`/modules/analytics\` - Statistiques et attribution
- \`/modules/sms_mailing\` - Envoi SMS via B2SMS
- \`/modules/admin\` - Administration système

## 👥 Rôles & Permissions

| Rôle | Description | Accès |
|------|-------------|-------|
| SUPADMIN | Super administrateur plateforme | Tous les clients, configuration globale, logs système |
| admin | Administrateur client | Gestion de son client uniquement |
| user | Utilisateur standard | Création de campagnes, consultation analytics |

## 🔗 API B2SMS

### Configuration
- **URL:** https://www.b2sms.com/v1
- **Auth:** HTTP Basic (login/password)
- **Format numéro:** MSISDN avec + (+262692XXXXXX)

### Endpoints utilisés
- \`GET /ping\` - Vérification des credentials
- \`POST /sms\` - Envoi d'un SMS

---
*Documentation générée depuis B2RCS*
`;
    
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'B2RCS_Documentation.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Fichier Markdown téléchargé');
  };

  const exportToHTML = () => {
    const content = generateDocumentationHTML();
    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Documentation B2RCS</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; background: #f8fafc; }
    .documentation-content { background: white; padding: 40px; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    h1 { color: #1e293b; border-bottom: 3px solid #4f46e5; padding-bottom: 15px; }
    h2 { color: #334155; margin-top: 35px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
    h3 { color: #475569; }
    code { background: #f1f5f9; padding: 3px 8px; border-radius: 4px; font-size: 13px; color: #7c3aed; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; }
    th { background: #f8fafc; font-weight: 600; }
    ul { padding-left: 25px; }
    li { margin: 8px 0; }
    hr { border: none; border-top: 1px solid #e2e8f0; margin: 30px 0; }
  </style>
</head>
<body>
  ${content}
</body>
</html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'B2RCS_Documentation.html';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Fichier HTML téléchargé');
  };

  const copyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast.success('Lien copié !');
    }
  };

  const revokeShare = async (token_to_revoke) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/admin/doc-share/${token_to_revoke}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Lien révoqué');
        loadExistingShares();
      }
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const sections = [
    { id: 'changelog', label: 'Historique (Récent)', icon: History },
    { id: 'overview', label: 'Vue d\'ensemble', icon: Book },
    { id: 'architecture', label: 'Architecture', icon: Server },
    { id: 'database', label: 'Base de données', icon: Database },
    { id: 'modules', label: 'Modules Backend', icon: LayoutIcon },
    { id: 'frontend', label: 'Frontend', icon: Code },
    { id: 'integrations', label: 'Intégrations', icon: Plug },
    { id: 'roles', label: 'Rôles & Permissions', icon: Users },
    { id: 'api', label: 'API Reference', icon: ExternalLink },
    { id: 'roadmap', label: 'Roadmap', icon: Map },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="documentation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Book className="h-7 w-7 text-indigo-600" />
              Documentation B2RCS
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Documentation technique complète de la plateforme
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Share Button - SUPADMIN only */}
            {isSuperAdmin && (
              <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline"
                    className={`${isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}`}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Partager
                  </Button>
                </DialogTrigger>
                <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                  <DialogHeader>
                    <DialogTitle className={isDarkMode ? 'text-white' : ''}>
                      <Share2 className="h-5 w-5 inline mr-2" />
                      Créer un lien de partage
                    </DialogTitle>
                    <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                      Le lien sera protégé par un mot de passe et aura une durée de validité limitée.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    {/* Password */}
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>
                        <Lock className="h-4 w-4 inline mr-2" />
                        Mot de passe de protection *
                      </Label>
                      <div className="relative">
                        <Input
                          type={showSharePassword ? "text" : "password"}
                          value={sharePassword}
                          onChange={(e) => setSharePassword(e.target.value)}
                          placeholder="Minimum 4 caractères"
                          className={`pr-10 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSharePassword(!showSharePassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                          {showSharePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Validity */}
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>
                        <Clock className="h-4 w-4 inline mr-2" />
                        Durée de validité
                      </Label>
                      <Select value={shareValidity} onValueChange={setShareValidity}>
                        <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                          <SelectItem value="24h">24 heures</SelectItem>
                          <SelectItem value="72h">72 heures (3 jours)</SelectItem>
                          <SelectItem value="7d">7 jours</SelectItem>
                          <SelectItem value="unlimited">Illimité</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Generated Link */}
                    {generatedLink && (
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                        <Label className="text-emerald-600 mb-2 block">Lien créé !</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={generatedLink}
                            readOnly
                            className={`text-xs ${isDarkMode ? 'bg-[hsl(220,14%,14%)]' : 'bg-white'}`}
                          />
                          <Button size="sm" variant="outline" onClick={copyLink}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          N'oubliez pas de communiquer le mot de passe séparément !
                        </p>
                      </div>
                    )}
                    
                    {/* Existing Shares */}
                    {existingShares.length > 0 && (
                      <div className="space-y-2">
                        <Label className={isDarkMode ? 'text-slate-300' : ''}>Liens actifs</Label>
                        <div className="max-h-32 overflow-y-auto space-y-2">
                          {existingShares.map((share, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center justify-between p-2 rounded-lg text-xs ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}
                            >
                              <div className="flex items-center gap-2">
                                <Link className="h-3 w-3 text-slate-400" />
                                <span className={isDarkMode ? 'text-slate-300' : ''}>{share.validity}</span>
                                <Badge variant="outline" className="text-xs">
                                  <Eye className="h-3 w-3 mr-1" />
                                  {share.access_count}
                                </Badge>
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => revokeShare(share.token)}
                                className="h-6 px-2 text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button
                      onClick={createShareLink}
                      disabled={shareLoading || !sharePassword}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {shareLoading ? 'Création...' : 'Créer le lien'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Export buttons - SUPADMIN only */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={() => exportToPDF()}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                title="Exporter en PDF"
              >
                <FileDown className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => exportToMarkdown()}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                title="Exporter en Markdown"
              >
                <FileCode className="h-4 w-4 mr-2" />
                Markdown
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => exportToHTML()}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                title="Exporter en HTML"
              >
                <FileText className="h-4 w-4 mr-2" />
                HTML
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar Navigation */}
          <div className="col-span-3">
            <Card className={`sticky top-24 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-indigo-600 text-white'
                          : isDarkMode
                            ? 'text-slate-300 hover:bg-[hsl(220,14%,20%)]'
                            : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <section.icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{section.label}</span>
                      {activeSection === section.id && (
                        <ChevronRight className="h-4 w-4 ml-auto" />
                      )}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <Card className={`${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <ScrollArea className="h-[calc(100vh-200px)]">
                <CardContent className="p-8">
                  {/* Overview */}
                  {activeSection === 'overview' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Vue d'ensemble
                        </h2>
                        <Badge variant="outline" className="text-xs">
                          Dernière MAJ: {formatDate(LAST_UPDATE)}
                        </Badge>
                      </div>
                      
                      <p className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                        B2RCS.com est une plateforme professionnelle de messagerie RCS (Rich Communication Services) 
                        permettant aux entreprises d'envoyer des messages enrichis à leurs clients via le protocole 
                        Google RCS Business Messaging.
                      </p>

                      <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Stack Technique</h3>
                          <table className="w-full text-sm">
                            <tbody className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                              <tr><td className="py-1 font-medium">Frontend</td><td>React + Tailwind + Shadcn/UI</td></tr>
                              <tr><td className="py-1 font-medium">Backend</td><td>FastAPI (Python)</td></tr>
                              <tr><td className="py-1 font-medium">Base de données</td><td>MongoDB</td></tr>
                              <tr><td className="py-1 font-medium">Email</td><td>Resend API</td></tr>
                              <tr><td className="py-1 font-medium">SMS</td><td>B2SMS API (www.b2sms.com/v1)</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Architecture */}
                  {activeSection === 'architecture' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Architecture Technique
                      </h2>
                      
                      <div className={`p-4 rounded-xl font-mono text-sm ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
                        <pre>{`/app/
├── backend/
│   ├── modules/           # 15+ modules métier
│   │   ├── analytics/     # Tableau de bord
│   │   ├── archives/      # Historique
│   │   ├── audit/         # Logs (56 types)
│   │   ├── auth/          # JWT + 2FA
│   │   ├── billing/       # Facturation
│   │   ├── blacklist/     # Gestion STOP
│   │   ├── campaigns/     # Orchestration
│   │   ├── iam/           # Users & Clients
│   │   ├── notification/  # Alertes
│   │   ├── rcs/           # Core messaging
│   │   ├── routing/       # SMS Fallback
│   │   └── ...
│   └── server.py
│
├── frontend/
│   └── src/
│       ├── components/    # UI Components
│       ├── contexts/      # React Contexts
│       └── pages/         # Pages
│
└── memory/
    └── PRD.md`}</pre>
                      </div>

                      <h3 className={`text-lg font-semibold mt-6 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Flux de données
                      </h3>
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                        <div className="flex items-center justify-center gap-4 text-sm">
                          <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">Frontend (React)</div>
                          <span>→</span>
                          <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">Backend (FastAPI)</div>
                          <span>→</span>
                          <div className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium">MongoDB</div>
                        </div>
                        <div className="flex justify-center mt-4">
                          <div className="text-center">
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Services externes</span>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">Resend</Badge>
                              <Badge variant="outline">B2SMS</Badge>
                              <Badge variant="outline">Google RCS</Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Database */}
                  {activeSection === 'database' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Base de Données MongoDB
                      </h2>
                      
                      <p className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                        L'application utilise MongoDB comme base de données NoSQL avec le driver Motor (async).
                      </p>

                      {/* Collections Overview */}
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Collections
                      </h3>
                      
                      <div className="grid gap-4">
                        {/* users collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>users</h4>
                            <Badge variant="outline">Authentification</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  email: String (unique),
  password_hash: String,
  name: String,
  role: "superadmin" | "admin" | "user",
  client_id: ObjectId | null,
  is_active: Boolean,
  two_factor_enabled: Boolean,
  created_at: DateTime,
  last_login: DateTime
}`}
                          </code>
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Index: email (unique), client_id
                          </p>
                        </div>

                        {/* clients collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>clients</h4>
                            <Badge variant="outline">Entreprises</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  name: String,
  company_name: String,
  email: String,
  is_active: Boolean,
  sms_fallback_config: {
    enabled: Boolean,
    sender_id: String (max 11 chars),
    credentials: {
      login: String,
      password: String (encrypted),
      service_url: String
    }
  },
  rcs_config: {
    agent_id: String,
    brand_id: String
  },
  unsubscribe_notification_config: {
    enabled: Boolean,
    mode: "instant" | "daily_recap",
    recap_time: String (HH:mm),
    emails: [String]
  },
  created_at: DateTime
}`}
                          </code>
                        </div>

                        {/* campaigns collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>campaigns</h4>
                            <Badge variant="outline">RCS/SMS</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  id: String (custom ID: camp_YYYYMMDD_xxx),
  name: String,
  client_id: String,
  message_type: "text" | "rich_card" | "carousel",
  content: {
    text: String,
    media_url: String,
    suggestions: [Object]
  },
  status: "draft" | "scheduled" | "sending" | 
          "sent" | "completed" | "failed",
  recipients: [String] (phone numbers),
  total_recipients: Number,
  sent_count: Number,
  delivered_count: Number,
  read_count: Number,
  failed_count: Number,
  shared_with: [{
    user_id: String,
    user_name: String,
    shared_at: DateTime,
    instructions: String (HTML)
  }],
  scheduled_at: DateTime,
  created_at: DateTime,
  completed_at: DateTime
}`}
                          </code>
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Index: client_id, status, created_at
                          </p>
                        </div>

                        {/* notifications collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>notifications</h4>
                            <Badge variant="outline">In-App</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  type: "campaign_shared" | "alert" | "system",
  title: String,
  message: String,
  user_id: String,
  client_id: String,
  priority: "low" | "medium" | "high",
  read: Boolean,
  dismissed: Boolean,
  data: Object (context-specific),
  action_url: String,
  created_at: DateTime
}`}
                          </code>
                        </div>

                        {/* blacklist collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>blacklist</h4>
                            <Badge variant="outline">STOP</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  phone: String (normalized E.164),
  client_id: String,
  source: "user" | "rcs_stop" | "sms_stop" | "import",
  reason: String,
  created_at: DateTime,
  created_by: String (user_id)
}`}
                          </code>
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Index: phone + client_id (compound unique)
                          </p>
                        </div>

                        {/* sms_campaigns collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>sms_campaigns</h4>
                            <Badge variant="outline">SMS Mailing</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  name: String,
  type: "marketing" | "transactional",
  message: String,
  sender_id: String,
  recipients_count: Number,
  send_mode: "immediate" | "scheduled" | "recurring",
  scheduled_at: DateTime,
  recurring: {
    pattern: "daily" | "weekly" | "monthly",
    days: [Number],
    time: String
  },
  status: "pending" | "sent" | "scheduled" | 
          "recurring" | "mock_sent" | "error",
  user_id: String,
  sent_count: Number,
  b2sms_response: Object,
  created_at: DateTime,
  sent_at: DateTime
}`}
                          </code>
                        </div>

                        {/* sms_landing_pages collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>sms_landing_pages</h4>
                            <Badge variant="outline">Fallback</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  slug: String (unique, URL-friendly),
  campaign_id: String,
  client_id: String,
  content: {
    title: String,
    body: String,
    media_url: String,
    cta_text: String,
    cta_url: String
  },
  expires_at: DateTime,
  views: Number,
  created_at: DateTime
}`}
                          </code>
                        </div>

                        {/* templates collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>templates</h4>
                            <Badge variant="outline">Messages</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  name: String,
  client_id: String,
  message_type: String,
  content: Object,
  category: String,
  tags: [String],
  is_favorite: Boolean,
  version: Number,
  created_by: String,
  created_at: DateTime,
  updated_at: DateTime
}`}
                          </code>
                        </div>

                        {/* audit_logs collection */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>audit_logs</h4>
                            <Badge variant="outline">56 types</Badge>
                          </div>
                          <code className={`block text-xs p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
{`{
  _id: ObjectId,
  event_type: String (LOGIN, CAMPAIGN_CREATE, etc.),
  user_id: String,
  client_id: String,
  resource_type: String,
  resource_id: String,
  details: Object,
  ip_address: String,
  user_agent: String,
  created_at: DateTime
}`}
                          </code>
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            TTL Index: created_at (90 jours par défaut)
                          </p>
                        </div>
                      </div>

                      {/* Best Practices */}
                      <h3 className={`text-lg font-semibold mt-6 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Bonnes Pratiques
                      </h3>
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                        <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          <li>• <strong>ObjectId</strong> : Toujours exclure _id des réponses JSON ou le convertir en string</li>
                          <li>• <strong>DateTime</strong> : Utiliser UTC (timezone.utc) pour toutes les dates</li>
                          <li>• <strong>Soft Delete</strong> : Préférer le changement de status plutôt que la suppression physique</li>
                          <li>• <strong>Indexes</strong> : Créer des index composites pour les requêtes fréquentes (client_id + status)</li>
                          <li>• <strong>TTL</strong> : Utiliser des index TTL pour les logs et données temporaires</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Modules */}
                  {activeSection === 'modules' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Modules Backend
                      </h2>
                      
                      <div className="grid gap-4">
                        {[
                          { name: 'IAM', prefix: '/api/clients', desc: 'Users, Clients, SMS Config' },
                          { name: 'Campaigns', prefix: '/api/v2/campaigns', desc: 'Création, envoi, partage' },
                          { name: 'Blacklist', prefix: '/api/v2/blacklist', desc: 'Gestion STOP, notifications' },
                          { name: 'Notifications', prefix: '/api/v2/notifications', desc: 'Alertes in-app' },
                          { name: 'Analytics', prefix: '/api/v2/analytics', desc: 'Dashboard, métriques' },
                          { name: 'Routing', prefix: '/api/v2/routing', desc: 'SMS Fallback, landing pages' },
                          { name: 'Audit', prefix: '/api/v2/audit', desc: '56 types d\'événements' },
                          { name: 'Templates', prefix: '/api/v2/templates', desc: 'Versioning, favoris' },
                        ].map((module) => (
                          <div key={module.name} className={`flex items-center justify-between p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                            <div>
                              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{module.name}</h3>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{module.desc}</p>
                            </div>
                            <code className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-[hsl(220,14%,25%)] text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                              {module.prefix}
                            </code>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Frontend */}
                  {activeSection === 'frontend' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Fonctionnalités Frontend
                      </h2>

                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>🌙 Thème Sombre Anthracite</h3>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Toggle soleil/lune dans le header. Variables CSS personnalisées pour un rendu professionnel.
                          </p>
                        </div>

                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>📝 Éditeur Rich Text (TipTap)</h3>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Gras, italique, souligné, titres, listes, citations, emojis. Utilisé pour partage de campagnes et messages.
                          </p>
                        </div>

                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>🔔 Notifications Animées</h3>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Cloche avec animation bounce et changement de couleur pour les nouvelles notifications.
                          </p>
                        </div>

                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>📤 Partage de Campagne</h3>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Modal avec sélection utilisateurs, instructions riches, notification email et in-app.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Integrations */}
                  {activeSection === 'integrations' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Intégrations Tierces
                      </h2>

                      <div className="grid gap-6">
                        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-white border-slate-200'}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                              <span className="text-purple-600 font-bold">R</span>
                            </div>
                            <div>
                              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Resend</h3>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Service d'envoi d'emails</p>
                            </div>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Utilisé pour : notifications de partage, reset password, récapitulatifs STOP.
                          </p>
                        </div>

                        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-white border-slate-200'}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                              <span className="text-amber-600 font-bold">B2</span>
                            </div>
                            <div>
                              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>B2SMS</h3>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>API SMS professionnelle</p>
                            </div>
                          </div>
                          <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Utilisé pour : SMS Fallback RCS, campagnes SMS Mailing.
                          </p>
                          <div className={`text-xs p-2 rounded ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-100'}`}>
                            <strong>Endpoints :</strong> /sms, /sms/list, /sms/bulk, /ping
                          </div>
                        </div>

                        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : 'bg-white border-slate-200'}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-bold">G</span>
                            </div>
                            <div>
                              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Google RCS</h3>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Rich Communication Services</p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">MOCKÉ</Badge>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Messages enrichis : texte, rich cards, carrousels, boutons d'action.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Roles */}
                  {activeSection === 'roles' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Gestion des Rôles
                      </h2>

                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl border-2 border-indigo-500 ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                          <h3 className={`font-bold text-indigo-600 mb-2`}>SUPERADMIN</h3>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>• Accès total à la plateforme</li>
                            <li>• Gestion de tous les clients</li>
                            <li>• Configuration système globale</li>
                            <li>• Logs et audit centralisés</li>
                          </ul>
                        </div>

                        <div className={`p-4 rounded-xl border-2 border-emerald-500 ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                          <h3 className={`font-bold text-emerald-600 mb-2`}>ADMIN</h3>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>• Gestion de SON client uniquement</li>
                            <li>• Création d'utilisateurs</li>
                            <li>• Configuration SMS</li>
                            <li>• Accès aux campagnes du client</li>
                          </ul>
                        </div>

                        <div className={`p-4 rounded-xl border-2 border-slate-400 ${isDarkMode ? 'bg-slate-500/10' : 'bg-slate-50'}`}>
                          <h3 className={`font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'} mb-2`}>USER</h3>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            <li>• Opérations quotidiennes</li>
                            <li>• Création et envoi de campagnes</li>
                            <li>• Accès limité aux paramètres</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* API Reference */}
                  {activeSection === 'api' && (
                    <div className="space-y-6">
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        API Reference
                      </h2>

                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Authentification</h3>
                          <code className={`block text-sm p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
                            POST /api/auth/login<br/>
                            {`{ "email": "...", "password": "..." }`}<br/><br/>
                            Headers: Authorization: Bearer {'<token>'}
                          </code>
                        </div>

                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Campagnes</h3>
                          <code className={`block text-sm p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
                            GET  /api/v2/campaigns<br/>
                            POST /api/v2/campaigns<br/>
                            POST /api/v2/campaigns/{'{id}'}/share<br/>
                            POST /api/v2/campaigns/{'{id}'}/send
                          </code>
                        </div>

                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Clients & SMS Config</h3>
                          <code className={`block text-sm p-3 rounded ${isDarkMode ? 'bg-[hsl(220,14%,10%)] text-green-400' : 'bg-slate-900 text-green-400'}`}>
                            GET  /api/clients<br/>
                            GET  /api/clients/{'{id}'}/sms-config<br/>
                            PUT  /api/clients/{'{id}'}/sms-config
                          </code>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Changelog */}
                  {activeSection === 'changelog' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Historique des Modifications
                        </h2>
                        <Badge variant="outline" className="text-xs">
                          Dernière MAJ: {formatDate(LAST_UPDATE)}
                        </Badge>
                      </div>

                      <div className="space-y-4">
                        {/* 22 Jan 2026 - 11h00 (PLUS RÉCENT EN PREMIER) */}
                        <div className={`p-4 rounded-xl border-l-4 border-red-500 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-red-100 text-red-700">22 Jan 2026 - 11:00</Badge>
                            <Badge variant="outline" className="text-xs">Session actuelle</Badge>
                          </div>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>✅ Bug fix: Sélecteur client RCS - ajout option "Aucun client"</li>
                            <li>✅ Bug fix: Format numéro SMS B2SMS (normalisation +262/+33)</li>
                            <li>✅ Module Logs SUPADMIN créé (/logs)</li>
                            <li>✅ Menu sidebar avec versioning auto-reset (v2.3)</li>
                            <li>✅ Documentation restructurée (historique décroissant)</li>
                            <li>🔍 Audit envoi emails Resend (pas de boucle détectée)</li>
                          </ul>
                        </div>

                        {/* 22 Jan 2026 - Matin */}
                        <div className={`p-4 rounded-xl border-l-4 border-indigo-500 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-indigo-100 text-indigo-700">22 Jan 2026 - 09:00</Badge>
                          </div>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>✅ Bug fix URL B2SMS (doc.b2sms.com → www.b2sms.com/v1)</li>
                            <li>✅ Bibliothèque Templates restaurée (42 templates)</li>
                            <li>✅ Page Campagnes: tri, suppression, édition</li>
                            <li>✅ Partage de campagne avec modal complet</li>
                            <li>✅ Éditeur riche TipTap avec emojis</li>
                            <li>✅ Bug fix SMS Fallback SuperAdmin</li>
                          </ul>
                        </div>

                        {/* 21 Jan 2026 */}
                        <div className={`p-4 rounded-xl border-l-4 border-emerald-500 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-emerald-100 text-emerald-700">21 Jan 2026</Badge>
                          </div>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>✅ Menu SUPADMIN avec mode édition</li>
                            <li>✅ Client obligatoire pour campagnes</li>
                            <li>✅ Notifications de désabonnement</li>
                            <li>✅ Modules grisés si pas de client sélectionné</li>
                            <li>✅ Notifications animées</li>
                          </ul>
                        </div>

                        {/* 20 Jan 2026 */}
                        <div className={`p-4 rounded-xl border-l-4 border-amber-500 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-amber-100 text-amber-700">20 Jan 2026</Badge>
                          </div>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>✅ SMS Fallback avec landing pages</li>
                            <li>✅ Preview SMS avant envoi</li>
                            <li>✅ Module SMS Mailing initialisé</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Roadmap */}
                  {activeSection === 'roadmap' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Roadmap
                        </h2>
                        <Badge variant="outline" className="text-xs">
                          Dernière MAJ: {formatDate(LAST_UPDATE)}
                        </Badge>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            <span className="w-3 h-3 rounded-full bg-green-500"></span>
                            ✅ Complété (22 Jan 2026)
                          </h3>
                          <ul className={`text-sm space-y-2 ml-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>✅ Module Logs SUPADMIN</li>
                            <li>✅ Correction URL B2SMS</li>
                            <li>✅ Restauration 42 templates</li>
                            <li>✅ Documentation en ligne</li>
                          </ul>
                        </div>

                        <div>
                          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            <span className="w-3 h-3 rounded-full bg-red-500"></span>
                            P1 - En cours
                          </h3>
                          <ul className={`text-sm space-y-2 ml-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>• Envoi réel SMS via B2SMS (test en cours)</li>
                            <li>• Migration API v1 → v2</li>
                            <li>• Menu sidebar avec items Logs/Documentation visibles</li>
                          </ul>
                        </div>

                        <div>
                          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                            P2 - À Faire
                          </h3>
                          <ul className={`text-sm space-y-2 ml-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>• Module SMS Mailing complet (upload CSV/XLSX)</li>
                            <li>• UI complète Archives/Blacklist</li>
                            <li>• Debug emails reset password</li>
                          </ul>
                        </div>

                        <div>
                          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            P3 - Futur
                          </h3>
                          <ul className={`text-sm space-y-2 ml-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <li>• Internationalisation (i18n)</li>
                            <li>• Webhooks Google RCS réels</li>
                            <li>• Optimisation performance</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </ScrollArea>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Documentation;
